// Ungelesene Chat-Nachrichten in der Kopfleiste. Vorher meldete sich der Chat nur über
// den Punkt der Pille — der leuchtet aber auch für Anrufe und Benachrichtigungen und war
// damit im Alltag dauerhaft an. Hier hat der Chat seinen eigenen, eindeutigen Ort.
import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useChat } from '../../../contexts/ChatContext';
import { useIslandActions } from '../../island/IslandProvider';
import {
  gruppenName, kollegeName, mitUngelesenem, vorschauText, zeitKurz, initialen,
} from '../../../lib/chat/nachrichten';

export const ChatGlocke: React.FC = () => {
  const { unterhaltungen, kollegen, gruppen, ungelesen, oeffneZiel } = useChat();
  const { openDeck } = useIslandActions();
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aussen = (e: MouseEvent) => {
      if (huelle.current && !huelle.current.contains(e.target as Node)) setOffen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('mousedown', aussen);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', aussen); document.removeEventListener('keydown', esc); };
  }, []);

  const offeneGespraeche = mitUngelesenem(unterhaltungen);

  const springe = (ziel: { art: 'person' | 'gruppe'; id: string }) => {
    setOffen(false);
    oeffneZiel(ziel);
    openDeck('chat');
  };

  const nameVon = (u: { art: 'person' | 'gruppe'; id: string }): string => {
    if (u.art !== 'gruppe') return kollegeName(kollegen.find((k) => k.id === u.id));
    const g = gruppen.find((x) => x.id === u.id);
    return g ? gruppenName(g) : 'Gruppe';
  };

  return (
    <div className="relative shrink-0" ref={huelle}>
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-label={ungelesen > 0 ? `${ungelesen} ungelesene Nachrichten` : 'Chat'}
        className="relative flex h-8 w-8 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-white/60 hover:text-[#0f172a]"
      >
        <MessageSquare size={17} strokeWidth={1.8} />
        {ungelesen > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex min-w-[15px] items-center justify-center rounded-full bg-[#c4a77d] px-1 text-[9px] font-black text-[#1a1d24]">
            {ungelesen > 99 ? '99+' : ungelesen}
          </span>
        )}
      </button>

      {offen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-white/50 bg-white/85 shadow-[0_16px_44px_-16px_rgba(15,23,42,0.35)] backdrop-blur-2xl animate-in fade-in slide-in-from-top-1 duration-150">
          <div className="border-b border-white/50 px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.12em] text-gray-400">
            Nachrichten
          </div>
          {offeneGespraeche.length === 0 ? (
            <div className="px-4 py-4 text-[12px] font-semibold text-gray-400">Nichts Ungelesenes.</div>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              {offeneGespraeche.map((u) => {
                const name = nameVon(u);
                return (
                  <button
                    key={`${u.art}:${u.id}`}
                    type="button"
                    onClick={() => springe({ art: u.art, id: u.id })}
                    className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-white/70"
                  >
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#1a1d24] text-[10px] font-black text-[#c4a77d]">
                      {initialen(name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-[12px] font-bold text-[#0f172a]">{name}</span>
                        <span className="shrink-0 text-[10px] font-semibold text-gray-400">
                          {u.letzte ? zeitKurz(u.letzte.created_at) : ''}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-[11px] font-semibold text-gray-500">
                          {u.letzte ? vorschauText(u.letzte) : 'Neue Nachricht'}
                        </span>
                        <span className="ml-auto shrink-0 rounded-full bg-[#c4a77d] px-1.5 text-[9px] font-black text-[#1a1d24]">
                          {u.ungelesen}
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
